import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { api, invalidateData, useMeta } from '../../api.ts';
import { bandModeFixedSchema, optionalCount } from '../../../shared/schemas.ts';
import { ErrorBox, Help, Loading, Spinner, useToast } from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';
import { useFixed } from './FixedOhcwPage.tsx';

/** Lomakkeen kenttänimi: b2000_CW (band * 100, ettei pisteitä tule nimeen) */
const key = (band: number, mode: string) => `b${Math.round(band * 100)}_${mode}`;
type Values = Record<string, string>;

export function FixedBandModePage() {
  const { user } = useUserContext();
  const meta = useMeta();
  const fixed = useFixed(user.id);
  const toast = useToast();
  const { register, handleSubmit, reset, setError, formState } = useForm<Values>();

  // Vain päämoodit (CW, PHONE, DIGI); taulukoissa käytetään CW- ja PHONE-arvoja
  const modes = useMemo(() => (meta.data?.modes ?? []).filter((m) => !m.parentMode).map((m) => m.mode), [meta.data]);
  const bands = meta.data?.bands ?? [];

  useEffect(() => {
    if (!fixed.data) return;
    const v: Values = { digiAll: fixed.data.digiAll === null ? '' : String(fixed.data.digiAll) };
    for (const r of fixed.data.bandMode) v[key(r.band, r.mode)] = String(r.value);
    reset(v);
  }, [fixed.data, reset]);

  const save = useMutation({
    mutationFn: (body: unknown) => api.put(`/users/${encodeURIComponent(user.id)}/fixed/band-mode`, body),
    onSuccess: () => {
      invalidateData();
      toast.ok('Band-Mode-arvot tallennettu');
    },
    onError: toast.error,
  });

  const onSubmit = (v: Values) => {
    let invalid = false;
    const values = bands.flatMap((b) =>
      modes.map((mode) => {
        const res = optionalCount.safeParse(v[key(b.band, mode)]);
        if (!res.success) {
          invalid = true;
          setError(key(b.band, mode), { message: res.error.issues[0]?.message });
        }
        return { band: b.band, mode, value: res.success ? res.data : null };
      }),
    );
    if (invalid) return;
    const parsed = bandModeFixedSchema.safeParse({ values, digiAll: v.digiAll });
    if (!parsed.success) {
      setError('digiAll', { message: parsed.error.issues[0]?.message });
      return;
    }
    save.mutate(parsed.data);
  };

  if (meta.isLoading || fixed.isLoading) return <Loading />;
  if (fixed.error) return <ErrorBox error={fixed.error} />;
  const errors = formState.errors;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="stack" noValidate>
      <div className="row">
        <p className="muted" style={{ margin: 0, flex: 1 }}>
          Syötä bandikohtaiset maamäärät, jos et ole ladannut logia tai logisi on osittainen – suurempi arvo (syötetty tai QSO:ista
          laskettu) näytetään OH Band-Modes -taulukossa. <strong>0 (nolla)</strong> piilottaa sinut kyseiseltä riviltä.
        </p>
        <Help name="OmatFixed" />
      </div>
      <div className="table-wrap" style={{ maxWidth: 560 }}>
        <table className="table compact">
          <thead>
            <tr>
              <th>Band</th>
              {modes.map((m) => (
                <th key={m} className="num">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => (
              <tr key={b.band}>
                <td className="mono">{b.label}</td>
                {modes.map((m) => (
                  <td key={m} className="num">
                    <input
                      className="input input-sm input-num"
                      inputMode="numeric"
                      aria-label={`${b.label} ${m}`}
                      aria-invalid={!!errors[key(b.band, m)]}
                      title={errors[key(b.band, m)]?.message}
                      {...register(key(b.band, m))}
                    />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="mono">ALL</td>
              {modes.map((m) => (
                <td key={m} className="num">
                  {m === 'DIGI' && (
                    <input
                      className="input input-sm input-num"
                      inputMode="numeric"
                      aria-label="DIGI ALL"
                      aria-invalid={!!errors.digiAll}
                      {...register('digiAll')}
                    />
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {Object.keys(errors).length > 0 && <div className="alert alert-error">Tarkista punaisella merkityt kentät (kokonaisluku 0–1000).</div>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : 'Tallenna'}
        </button>
      </div>
    </form>
  );
}
